// AccordionPanel.js
import React, { useState, useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';
import AccordionHeader from './Header'; // Import the AccordionHeader component
import RenderAccordionField from './Fields'; // Import the new component
import { ABOUT_ME_CHAR_LIMIT, OTHERS_SECTION_CHAR_LIMIT } from '../../../../utils/constants';

const StyledAccordion = styled(({ showShadow, ...other }) => <Accordion {...other} />)(({ showShadow }) => ({
  width: '100%',
  borderRadius: '10px',
  border: '1px solid #e7e7ff',
  boxShadow: showShadow ? '0px 18px 30.5px 0px #C5C5FF5E' : 'none',
  '&:first-of-type': {
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
  },
  '&:last-of-type': {
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
  },
  '::before': {
    content: "none"
  }
}));


const AccordionPanel = ({
  title,
  isAddButtonVisible = true,
  isEditSaveToggleVisible = true,
  accordionDetailsPadding = false,
  shudDeleteBtnRender = true,
  shudClearBtnRender = true,
  initialFields = [],
  handleStagedData,
  setIsSaveDisabled,
  componentType,
  initShowShadow = false,
  setTrackUnstagedChanges,
  resetSectionFields
}) => {
  const [expanded, setExpanded] = useState('panel1');
  const [editBtnState, setEditBtnState] = useState(1); // 0: disabled, 1: active, 2: displayResetBtn
  const [addBtnState, setAddBtnState] = useState(1); // 0: disabled, 1: active, 2: displaySaveBtn
  const [fields, setFields] = useState(initialFields);
  const [stagedFields, setStagedFields] = useState([]);
  const [showShadow, setShowShadow] = useState(initShowShadow);
  const [pendingCharLimit, setPendingCharLimit] = useState(0);


  useEffect(() => {
    if (resetSectionFields) {
      setFields(initialFields);
      setAddBtnState(0);
      setEditBtnState(0);
    }
  }, [resetSectionFields]);


  useEffect(() => {
    setAddBtnState(1);
    const disabledFields = fields.map((field) => ({
      ...field,
      disabled: true,
    }));

    if (componentType === "aboutMe") {
      // Set remaining characters here
      setPendingCharLimit(ABOUT_ME_CHAR_LIMIT - initialFields[0].value?.length || 0);
    }
    setStagedFields(disabledFields);
    if (disabledFields.length) {
      setEditBtnState(1);
    } else {
      setEditBtnState(0);
    }
  }, []);

  useEffect(() => {
    if (addBtnState === 2) {
      // tracking staged state of the variable
      setTrackUnstagedChanges((prev) => ({ ...prev, [componentType]: true }));
    } else {
      setTrackUnstagedChanges((prev) => ({ ...prev, [componentType]: false }));
    }
  }, [addBtnState]);


  useEffect(() => {
    setShowShadow(initShowShadow);
  }, [initShowShadow]);


  const handleAddField = () => {
    setShowShadow(true);
    let newField = { id: Date.now(), value: '', componentType, disabled: false, isNew: true, isPristine: true };
    if (['satellite', 'land', 'mobile'].includes(componentType)) {
      newField = { ...newField, required: true };
    }
    setFields([...fields, newField]);
    setEditBtnState(2);
    setAddBtnState(0);
    setExpanded('panel1');
  };

  const handleEditField = () => {
    setShowShadow(true);
    setEditBtnState(2);
    setAddBtnState(0);
    setFields(fields.map((field) => ({ ...field, componentType, disabled: false })));
    setExpanded('panel1');
  };

  const handleFieldChange = (index, event, isFirstRender, fieldId) => {
    const currentFields = [...fields];
    const currentField = currentFields[index];
    currentField.value = event.target.value;

    if (componentType === "aboutMe") {
      if (event?.target?.value?.length > ABOUT_ME_CHAR_LIMIT) {
        return
      }
      setPendingCharLimit(ABOUT_ME_CHAR_LIMIT - event?.target?.value?.length)
    } else if (componentType === "form" && ["userCompanyName", "addressLine1", "city", "state", "postCode"].includes(fieldId)) {
      if (event?.target?.value?.length > OTHERS_SECTION_CHAR_LIMIT) {
        // even if the length of string in DB is > the OTHERS_SECTION_CHAR_LIMIT, below code suppresses it to the limit 
        currentField.value = event.target.value.slice(0, OTHERS_SECTION_CHAR_LIMIT);
      }
    }

    if (currentField.value) {
      if (currentField.required) {
        currentField.isRequiredAspectInvalid = false;
      }
    } else {
      if (currentField.required) {
        currentField.isRequiredAspectInvalid = true;
      }
    }
    if (!isFirstRender) currentField.isPristine = false;
    setFields(currentFields);
    const anyCurrFieldInvalid = currentFields.some((curField) => curField.required && curField.isRequiredAspectInvalid);
    if (anyCurrFieldInvalid || currentField.isInvalid) {
      setAddBtnState(0);
    } else {
      setAddBtnState(2);
    }
  };

  const handleSaveFields = () => {
    setAddBtnState(1);
    setIsSaveDisabled(false);
    setFields(() => {
      const toSaveFields = fields.map((field) => ({
        ...field,
        disabled: true,
        isNew: false
      }));
      setStagedFields(toSaveFields);
      if (toSaveFields.length) {
        setEditBtnState(1);
      } else {
        setEditBtnState(0);
      }
      handleStagedData(toSaveFields, componentType);
      return toSaveFields;
    });
    setShowShadow(false);
  };

  const handleResetClick = () => {
    stagedFields.length ? setEditBtnState(1) : setEditBtnState(0);
    setAddBtnState(1);
    setFields(stagedFields);
    setShowShadow(false);
  };

  const handleClearField = (index, e) => {
    let clearValue = '';
    const currentFields = [...fields];
    let currentField = currentFields[index];
    currentField.value = clearValue;
    if (currentField.required) {
      currentField.isRequiredAspectInvalid = true;
    }
    setFields(currentFields);
    setPendingCharLimit(ABOUT_ME_CHAR_LIMIT)
    const anyCurrFieldInvalid = currentFields.some((curField) => curField.required && curField.isRequiredAspectInvalid);
    if (anyCurrFieldInvalid) {
      setAddBtnState(0);
    } else {
      setAddBtnState(2);
    }
  };

  const handleDeleteField = (index) => {
    const isNew = fields[index].isNew;
    const currentFields = fields.filter((_, i) => i !== index);
    setFields(currentFields);
    const anyCurrFieldInvalid = currentFields.some((curField) => curField.required && curField.isRequiredAspectInvalid);

    if (isNew) {
      setAddBtnState(1);
    } else {
      anyCurrFieldInvalid ? setAddBtnState(0) : setAddBtnState(2);
    }
  };

  return (
    <StyledAccordion expanded={expanded === 'panel1'} elevation={0} showShadow={showShadow}>
      <AccordionHeader
        expanded={expanded}
        setExpanded={setExpanded}
        panelId="panel1"
        title={title}
        pendingCharLimit={pendingCharLimit}
        componentType={componentType}
        isAddButtonVisible={isAddButtonVisible}
        addBtnState={addBtnState}
        handleAddField={handleAddField}
        editBtnState={editBtnState}
        handleEditField={handleEditField}
        handleSaveFields={handleSaveFields}
        handleResetClick={handleResetClick}
        isEditSaveToggleVisible={isEditSaveToggleVisible}
      />
      <AccordionDetails sx={{ padding: accordionDetailsPadding ? '1rem' : '0 1rem' }}>
        {fields.map((field, index) => (
          <RenderAccordionField
            key={field.id || index}
            field={field}
            index={index}
            handleFieldChange={handleFieldChange}
            handleClearField={handleClearField}
            handleDeleteField={handleDeleteField}
            shudClearBtnRender={shudClearBtnRender}
            shudDeleteBtnRender={shudDeleteBtnRender}
            componentType={componentType}
          />
        ))}
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default AccordionPanel;
